import { useState, useCallback } from 'react'

import { useApp } from '../../../../app/AppContext.jsx'
import { useProduct } from '../../index.js'
import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { applySourceTransforms } from '../../../../shared/utils/applySourceTransforms.js'
import { getFileType } from '../../../../shared/utils/getFileType.js'

import { CollapsiblePane } from './CollapsiblePane.jsx'

import { Button } from '../../../../shared/ui/Button/index.js'
import { Download } from '../../../../shared/ui/icons/index.js'
import { MediaPreviewModal } from '../../../../shared/ui/MediaPreviewModal/index.js'
import { Toast } from '../../../../shared/ui/Toast/index.js'

import styles from './ResourcesPane.module.scss'

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Derive a filename from a URL. Falls back to the resource label.
 * @param {string} url
 * @param {string} fallbackLabel
 * @returns {string}
 */
function deriveFilename(url, fallbackLabel) {
  const clean = (url ?? '').split('?')[0].split('#')[0]
  const parts = clean.split('/')
  const last  = parts[parts.length - 1]
  return last || fallbackLabel
}

/**
 * Fetch a URL, validate PDF magic bytes (%PDF), return ArrayBuffer.
 * Throws if the response is not OK, empty, or not a valid PDF.
 * Needed because dev servers (Vite) return HTTP 200 + HTML for missing static files.
 *
 * @param {string} url
 * @returns {Promise<ArrayBuffer>}
 */
async function fetchValidatedPdf(url) {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const buf = await res.arrayBuffer()
  if (!buf || buf.byteLength === 0) throw new Error('Empty response')
  const magic = String.fromCharCode(...new Uint8Array(buf).subarray(0, 4))
  if (magic !== '%PDF') throw new Error('Not a valid PDF')
  return buf
}

// Registry of built-in resource types.
// buildUrl(base, uuid, stem) → server path — each type has its own naming convention:
//   floorplan_pdf:       file is always  `{uuid}.pdf`   (generator hardcodes uuid as filename)
//   energy_certificates: file is         `{stem}.pdf`   (stem = transforms applied to source_key)
const BUILT_INS = [
  {
    key:      'floorplan_pdf',
    labelKey: 'product.resource_floorplan_pdf',
    buildUrl: (base, uuid, _stem) => `${base}apartments/${uuid}/${uuid}.pdf`,
    buildFilenameSuffix: null,
  },
  {
    key:      'energy_certificates',
    labelKey: 'product.resource_energy_certificates',
    buildUrl: (base, uuid, _stem)  => `${base}apartments/${uuid}/${uuid}_epc.pdf`,
    buildFilenameSuffix: "epc",
  },
]

/**
 * Build the list of built-in (app_config) resource items for the current product.
 *
 * URL on server is determined by each entry's `buildUrl`.
 * Download filename (saved to user disk) is always `{stem}.pdf` derived from source_transforms.
 *
 * @param {object}   appConfigApartments  - `projectConfig.app_config.apartments`
 * @param {object}   product              - Current product object
 * @param {string}   projectDataUrl       - Base data URL (e.g. '/.dev_data/')
 * @param {Function} t                   - Translation function
 * @returns {Array<{id,label,url,filename,isBuiltIn:true}>}
 */
function buildBuiltInResources({
    appConfigApartments, 
    developerName,
    product, 
    projectDataUrl, 
    projectName,
    t
  }) {
  if (!product || !projectDataUrl) return []

  const base  = projectDataUrl.endsWith('/') ? projectDataUrl : projectDataUrl + '/'
  const items = []

  for (const { key, labelKey, buildUrl, buildFilenameSuffix } of BUILT_INS) {
    const entry = appConfigApartments?.[key]
    if (!entry?.enabled) continue

    const { source_key, source_transforms } = entry.config ?? {}
    if (!source_key) continue

    const rawValue    = String(product[source_key] ?? '')
    const transformed = applySourceTransforms(rawValue, source_transforms)
    if (transformed === null) continue  // excluded by filter_prefix

    const stem      = String(transformed)
    const url       = buildUrl(base, product.uuid, stem)
    const developerPrefix = developerName ? `${developerName}_` : ''
    const projectPrefix = projectName ? `${projectName}_` : ''
    const suffix    = buildFilenameSuffix ? `_${buildFilenameSuffix}` : ''
    const filename  = `${developerPrefix}${projectPrefix}${stem}${suffix}.pdf`  // save-as name on user disk

    items.push({ id: key, label: t(labelKey), url, filename, isBuiltIn: true })
  }

  return items
}

/**
 * Trigger a file download via Fetch → Blob → anchor click (preserves filename).
 * Falls back to window.open for cross-origin resources.
 *
 * @param {string}   url
 * @param {string}   filename
 * @param {Function} onError  - Called with an error message string on failure
 */
async function triggerDownload(url, filename, onError) {
  try {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob    = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a       = document.createElement('a')
    a.href        = blobUrl
    a.download    = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
  } catch {
    // CORS or network failure → open in new tab (reliable cross-origin fallback)
    const popup = window.open(url, '_blank', 'noopener,noreferrer')
    if (!popup) {
      onError()
    }
  }
}

// ── Resource row ───────────────────────────────────────────────────────────────

function ResourceRow({ resource, onPreview, onDownload, downloadAriaLabel, isSingleResource }) {
  const fileType = getFileType(resource.url)
  const isPreviewable = fileType === 'pdf' || fileType === 'image'

  const labelClass = [styles.resourceLabel, isSingleResource ? styles.singleResourceLabel : ''].filter(Boolean).join(' ')
  const rowClass = [styles.resourceRow, isSingleResource ? styles.singleResourceRow : ''].filter(Boolean).join(' ')

  return (
    <div className={rowClass}>
      {isPreviewable ? (
        <button
          type="button"
          className={labelClass}
          onClick={() => onPreview(resource)}
        >
          {resource.label}
        </button>
      ) : (
        <span className={labelClass}>{resource.label}</span>
      )}
      <Button
        type="button"
        kind="icon"
        size="small"
        variant="ghost"
        // className={styles.downloadBtn}
        onClick={() => onDownload(resource)}
        aria-label={downloadAriaLabel}
      >
        <Download />
      </Button>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * ResourcesPane — documents download section in the product unit card.
 *
 * Displays two categories of downloadable resources:
 *   - Built-in: floorplan_pdf and energy_certificates (from app_config.apartments)
 *     Filename is derived via source_key + source_transforms on the product.
 *   - CMS: additional_resources.resources[] from panelSettings (label + url, is_visible filter)
 *
 * Layout:
 *   - 0 resources → renders null
 *   - 1 resource  → inline row (no CollapsiblePane wrapper)
 *   - 2+ resources → CollapsiblePane list
 *
 * Click behaviour:
 *   - PDF / image → opens preview Modal (iframe / img)
 *   - Other file types → triggers direct download
 *   - Download button always triggers Fetch→Blob download (falls back to window.open)
 */
export function ResourcesPane() {
  const { t } = useI18n()
  const { projectConfig, panelSettings, windowData } = useApp()
  const { selection } = useProduct()
  const product = selection.product

  const [previewResource, setPreviewResource] = useState(null)
  const [toastMessage,    setToastMessage]    = useState(null)

  // ── Build resource list ──────────────────────────────────────────────────

  const appConfigApartments = projectConfig?.app_config?.apartments ?? {}
  const developerName = projectConfig?.developer_display_name ?? null
  const projectName = projectConfig?.name ?? null
  const projectDataUrl      = windowData?.projectDataUrl ?? null

  const builtInItems = buildBuiltInResources({
    appConfigApartments, 
    developerName,
    product, 
    projectDataUrl, 
    projectName,
    t
  })

  const cmsItems = (panelSettings?.additional_resources?.resources ?? [])
    .filter((r) => r.is_visible && r.label && r.url)
    .map((r, i) => ({
      id:        `cms_${i}`,
      label:     r.label,
      url:       r.url,
      filename:  null,   // derived from URL at download time
      isBuiltIn: false,
    }))

  const resources = [...builtInItems, ...cmsItems]

  if (resources.length === 0) return null

  // ── Handlers ─────────────────────────────────────────────────────────────

  // For built-in PDFs: fetch + validate before opening modal → store blobUrl on resource.
  // For CMS images / PDFs: open directly (server controls CORS).
  const handlePreview = useCallback(async (resource) => {
    const fileType = getFileType(resource.url)
    if (fileType === 'pdf' && resource.isBuiltIn) {
      try {
        const buf     = await fetchValidatedPdf(resource.url)
        const blobUrl = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
        setPreviewResource({ ...resource, blobUrl })
      } catch {
        setToastMessage(t('product.resource_pdf_not_found'))
      }
      return
    }
    setPreviewResource(resource)
  }, [t])

  const handleClosePreview = useCallback(() => {
    if (previewResource?.blobUrl) URL.revokeObjectURL(previewResource.blobUrl)
    setPreviewResource(null)
  }, [previewResource])

  // For built-in PDFs: validate before download so a missing file never saves as HTML.
  // For CMS resources: pass through to triggerDownload (server-controlled).
  const handleDownload = useCallback(async (resource) => {
    const filename = resource.filename ?? deriveFilename(resource.url, resource.label)
    if (resource.isBuiltIn) {
      try {
        const buf     = await fetchValidatedPdf(resource.url)
        const blobUrl = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
        const a       = document.createElement('a')
        a.href        = blobUrl
        a.download    = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
      } catch {
        setToastMessage(t('product.resource_download_error'))
      }
      return
    }
    await triggerDownload(resource.url, filename, () => {
      setToastMessage(t('product.resource_download_error'))
    })
  }, [t])

  // ── Render ────────────────────────────────────────────────────────────────

  const renderRows = (isSingleResource = false) =>
    resources.map((resource) => (
      <ResourceRow
        key={resource.id}
        resource={resource}
        onPreview={handlePreview}
        onDownload={handleDownload}
        downloadAriaLabel={t('product.resource_download_aria', { label: resource.label })}
        isSingleResource
      />
    ))

  const paneContent =
    resources.length === 1 ? (
      <div className={styles.inlineWrapper}>
        {renderRows(true)}
      </div>
    ) : (
      <CollapsiblePane
        title={t('product.pane_resources')}
        collapseAriaLabel={t('product.resource_collapse_aria')}
        expandAriaLabel={t('product.resource_expand_aria')}
      >
        <div className={styles.listWrapper}>
          {renderRows()}
        </div>
      </CollapsiblePane>
    )

  return (
    <>
      {paneContent}

      <MediaPreviewModal
        resource={previewResource}
        onClose={handleClosePreview}
        onDownload={handleDownload}
        closeLabel={t('product.resource_modal_close')}
        downloadLabel={t('product.resource_modal_download')}
      />

      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage(null)}
          variant="error"
        />
      )}
    </>
  )
}
